import {
  Alert,
  AlertDescription,
  AlertIcon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import {
  investorCreateFormData,
  investorCreateSchema,
} from "features/investors/InvestorSchemas";
import { useInvestorCreate } from "gen/endpoints/investor/investor";
import { getInvestorsTableDataRetrieveQueryKey } from "gen/endpoints/investor-table-data/investor-table-data";
import { InvestorCreateDataRequest } from "gen/model/investorCreateDataRequest";
import { InvestorReadData } from "gen/model/investorReadData";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { InfoBoxIcon } from "shared/icons/InfoBoxIcon";

interface InvestorAddModalProps {
  onCloseComplete: () => void;
  onSuccess: (data: InvestorReadData) => void;
}

const InvestorAddModal: React.FC<InvestorAddModalProps> = ({
  onSuccess,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["investor", "investorEntity"]);
  const queryClient = useQueryClient();

  const { mutateAsync: createInvestor } = useInvestorCreate();

  const form = useForm<InvestorCreateDataRequest>({
    // @ts-expect-error TODO: Convert to zod
    resolver: yupResolver(investorCreateSchema),
  });

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("investor:add.title")}</ModalHeader>
        <Form
          form={form}
          onSubmit={async (data: investorCreateFormData) => {
            const result = await createInvestor({
              data: {
                ...data,
                maybern_id: data.name,
              },
            });
            await queryClient.invalidateQueries({
              queryKey: getInvestorsTableDataRetrieveQueryKey(),
            });
            return result;
          }}
          onSuccess={onSuccess}
          successMessage={t("investor:add.success")}
        >
          <ModalBody>
            <Alert
              status="info"
              mb="24px"
              data-ls-enabled={true}
              data-public={true}
            >
              <AlertIcon>
                <InfoBoxIcon />
              </AlertIcon>
              <AlertDescription>{t("investor:add.alert")}</AlertDescription>
            </Alert>
            <FormTextInput name="name" title={t("investor:name")} />
            <FormTextInput
              name="primary_contact_email"
              type="email"
              title={t("investor:primaryContactEmail")}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} />}
              confirm={
                <FormSubmitButton>{t("investor:add.button")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default InvestorAddModal;
